import React, { FC } from "react";
import { ResponsiveLine } from "@nivo/line";
import { Box, makeStyles } from "@material-ui/core";
import { Text } from "../components/Text";
import colors from "../constants/colors";
import { observer } from "mobx-react-lite";
import { CrosshairLine } from "./CrossHairLine";
import { store } from "../store";
import { legendConfig } from "../constants/configs";

interface IChartProps {
  data: any;
  title: string;
  yLabel: string;
  xLabel: string;
  xAxisLabels: any[];
  yScale?: { min: number; max: number };
}

export const Chart: FC<IChartProps> = observer(
  ({ data, title, yLabel, xLabel, xAxisLabels, yScale }) => {
    const classes = styles();
    const { setX, hasSingleModel } = store;

    return (
      <Box className={classes.container}>
        <Text color="secondary" bold>
          {title}
        </Text>
        <ResponsiveLine
          data={data}
          enablePoints={false}
          animate
          lineWidth={2}
          enableGridX={true}
          gridXValues={xAxisLabels.map((x) => x.number)}
          onMouseMove={(point) => {
            setX(point.x);
          }}
          margin={{ top: 10, right: 25, bottom: 80, left: 50 }}
          xScale={{ type: "point" }}
          yScale={{
            type: "linear",
            min: yScale ? yScale?.min : "auto",
            max: yScale ? yScale?.max : "auto",
          }}
          axisTop={null}
          axisRight={null}
          layers={[
            "grid",
            "markers",
            "axes",
            "areas",
            (props) => <CrosshairLine {...props} />,
            "lines",
            "points",
            "slices",
            "mesh",
            "legends",
          ]}
          axisBottom={{
            tickSize: 1,
            tickPadding: 5,
            tickRotation: 0,
            legend: xLabel,
            legendOffset: 40,
            legendPosition: "middle",
            format: (values) => {
              const label = xAxisLabels.find(
                (label) => label.number === values
              );

              return label?.label || "";
            },
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: yLabel,
            legendOffset: -40,
            legendPosition: "middle",
          }}
          colors={data.map(({ color }) => color)}
          pointSize={10}
          pointColor={{ theme: "background" }}
          pointBorderWidth={2}
          pointBorderColor={{ from: "serieColor" }}
          pointLabel="y"
          pointLabelYOffset={-12}
          useMesh={true}
          // @ts-ignore
          legends={hasSingleModel ? legendConfig : []}
        />

        {!hasSingleModel && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="row-reverse"
            width="100%"
            marginTop={-2}
          >
            {data.map(({ id, color }, index: number) => (
              <Box
                key={`${id}-${index}`}
                display="flex"
                alignItems="center"
                marginLeft={1}
              >
                <Circle color={color} />
                <span className={classes.legends}>{id}</span>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  }
);

const Circle = ({ color }) => (
  <svg height="10" width="10">
    <circle cx="5" cy="6" r="4" fill={color} />
  </svg>
);

const styles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "30px",
    flex: 1,
    height: "300px",
    backgroundColor: colors.grey,
    borderRadius: 15,
    marginTop: 20,
    width: "calc(100% - 1em)",
    [theme.breakpoints.up("md")]: {
      flex: "initial",
      width: "calc(50% - 1em)",
    },
  },
  legends: {
    fontSize: 10,
    marginTop: 5,
    flex: 1,
    marginLeft: 2,
    [theme.breakpoints.down("md")]: {
      fontSize: 8,
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: 6.5,
      marginLeft: 0,
    },
  },
}));
